import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type {
  CompanySettings,
  CustomerDto,
  SalesInvoiceDto,
  SalesNoteDto,
  SalesNoteKind,
  SalesNoteSummary,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import { noteDocument, PrintPreview } from './PrintDocument';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  invoice: SalesInvoiceDto;
  /** للطباعة: بيانات العميل ورقمه الضريبى فى ترويسة الإشعار. */
  customer?: CustomerDto;
  /** null حتى تُقرأ الإعدادات — الأزرار لا تظهر قبلها. */
  settings: CompanySettings['sales'] | null;
  canIssueNotes: boolean;
  canCancel: boolean;
  onChanged: (invoice: SalesInvoiceDto) => void;
  onSessionLost: () => void;
}

const ISSUE_LABELS: Record<string, string> = {
  CREDIT_NOTES_DISABLED: 'الإشعار الدائن مطفأ فى الإعدادات',
  DEBIT_NOTES_DISABLED: 'الإشعار المدين مطفأ فى الإعدادات',
  INVOICE_CANCELLATION_DISABLED: 'إلغاء الفواتير مطفأ فى الإعدادات',
  CREDIT_NOTE_EXCEEDS_OPEN_BALANCE: 'الإشعار الدائن أكبر من المتبقى على الفاتورة',
  CREDIT_NOTE_EXCEEDS_INVOICE: 'مجموع الإشعارات الدائنة يتجاوز إجمالى الفاتورة',
  NOTE_BEFORE_INVOICE: 'تاريخ الإشعار قبل تاريخ الفاتورة',
  INVOICE_CANCELLED: 'الفاتورة ملغاة',
  INVOICE_ALREADY_CANCELLED: 'الفاتورة ملغاة بالفعل',
  INVOICE_HAS_RECEIPTS: 'على الفاتورة تحصيل — لا تُلغى، استخدم إشعارًا دائنًا',
  INVOICE_HAS_NOTES: 'على الفاتورة إشعارات — لا تُلغى',
  CANCELLATION_BEFORE_INVOICE: 'تاريخ الإلغاء قبل تاريخ الفاتورة',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
  DATE_OUTSIDE_PERIOD: 'التاريخ خارج الفترة المالية',
  INVOICE_REVENUE_ACCOUNT: 'حساب الإيراد يجب أن يكون حساب إيراد طرفيًا',
  INVOICE_ZERO_TOTAL: 'الإجمالى يجب أن يكون موجبًا',
  STOCK_RETURN_EXCEEDS_SOLD: 'الكمية المرتجعة أكبر مما بيع من الصنف على هذه الفاتورة',
  STOCK_BACKDATED: 'تاريخ الإشعار قبل آخر حركة لهذا الصنف',
};

const KIND_LABELS: Record<SalesNoteKind, string> = { credit: 'إشعار دائن', debit: 'إشعار مدين' };

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

type Mode = { kind: 'note'; noteKind: SalesNoteKind } | { kind: 'cancel' } | null;

/**
 * تعديلات الفاتورة المنشورة: إشعار دائن أو مدين، أو إلغاء بقيد عكسى. كل زر يظهر حسب
 * إعداد الشركة وصلاحية المستخدم وحال الفاتورة — والـ API يرفض على أى حال.
 */
export default function InvoiceAdjustments({
  companyId,
  currency,
  invoice,
  customer,
  settings,
  canIssueNotes,
  canCancel,
  onChanged,
  onSessionLost,
}: Props) {
  const [notes, setNotes] = useState<SalesNoteSummary[]>([]);
  const [printing, setPrinting] = useState<SalesNoteDto | null>(null);
  const [mode, setMode] = useState<Mode>(null);
  const [date, setDate] = useState(today);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        setProblems(
          issues.length
            ? issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message)
            : [caught.problem.detail ?? caught.problem.title],
        );
        return;
      }
      setProblems([caught instanceof Error ? caught.message : String(caught)]);
    },
    [onSessionLost],
  );

  const loadNotes = useCallback(() => {
    api.salesNotes(companyId, invoice.id).then(setNotes).catch(handle);
  }, [companyId, invoice.id, handle]);

  useEffect(loadNotes, [loadNotes]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setProblems([]);
  }

  function start(next: Mode): void {
    setMode(next);
    setReason('');
    setDescription('');
    setAmount('');
    setDate(today());
    touched();
  }

  const cancelled = invoice.status === 'cancelled';
  const showCredit = canIssueNotes && !cancelled && settings?.creditNotes === true;
  const showDebit = canIssueNotes && !cancelled && settings?.debitNotes === true;
  const showCancel =
    canCancel &&
    !cancelled &&
    settings?.invoiceCancellation === true &&
    Money.of(invoice.paid).isZero() &&
    notes.length === 0;

  const parsedAmount = parseAmount(amount);
  const ready =
    reason.trim().length >= 3 &&
    (mode?.kind === 'cancel' ||
      (description.trim() !== '' && parsedAmount !== null && Money.of(parsedAmount).isPositive()));

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!mode || !ready) return;
    setBusy(true);
    setProblems([]);
    try {
      if (mode.kind === 'cancel') {
        const updated = await api.cancelSalesInvoice(companyId, invoice.id, idempotencyKey, {
          cancellationDate: date,
          reason: reason.trim(),
        });
        onChanged(updated);
      } else {
        // الإشعار مبلغ بلا ضريبة حتى تُعتمد قواعدها (BLOCKED-TAX) — كالفاتورة من هذه الشاشة
        await api.createSalesNote(companyId, idempotencyKey, {
          kind: mode.noteKind,
          invoiceId: invoice.id,
          noteDate: date,
          reason: reason.trim(),
          lines: [{ description: description.trim(), quantity: '1', unitPrice: parsedAmount! }],
        });
        onChanged(await api.salesInvoice(companyId, invoice.id));
        loadNotes();
      }
      setMode(null);
    } catch (caught) {
      handle(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="no-print">
      {printing && (
        <PrintPreview
          companyId={companyId}
          doc={noteDocument(printing, customer)}
          onClose={() => setPrinting(null)}
          onSessionLost={onSessionLost}
        />
      )}
      <p className="sub">
        المحصّل {formatMoney(invoice.paid)} • إشعارات دائنة {formatMoney(invoice.creditNotesTotal)}{' '}
        • مدينة {formatMoney(invoice.debitNotesTotal)} • المتبقى{' '}
        <strong>{formatMoney(invoice.openBalance)}</strong> {currency}
      </p>
      {invoice.cancellation && (
        <p className="sub">
          <span className="pill bad">ملغاة</span> بتاريخ {invoice.cancellation.cancellationDate} —{' '}
          {invoice.cancellation.reason} • القيد العكسى {invoice.cancellation.journalNumber}
        </p>
      )}

      {notes.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>الإشعار</th>
                <th>النوع</th>
                <th>التاريخ</th>
                <th>السبب</th>
                <th>الإجمالى</th>
                <th>القيد</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => (
                <tr key={note.id}>
                  <td className="mono">{note.number}</td>
                  <td>{KIND_LABELS[note.kind]}</td>
                  <td>{note.noteDate}</td>
                  <td>{note.reason}</td>
                  <td className="num-cell">{formatMoney(note.total)}</td>
                  <td className="mono">{note.journalNumber}</td>
                  <td>
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        api.salesNote(companyId, note.id).then(setPrinting).catch(handle)
                      }
                    >
                      طباعة
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showCredit || showDebit || showCancel) && !mode && (
        <div className="form-actions">
          {showCredit && (
            <button
              type="button"
              className="ghost"
              onClick={() => start({ kind: 'note', noteKind: 'credit' })}
            >
              إشعار دائن
            </button>
          )}
          {showDebit && (
            <button
              type="button"
              className="ghost"
              onClick={() => start({ kind: 'note', noteKind: 'debit' })}
            >
              إشعار مدين
            </button>
          )}
          {showCancel && (
            <button type="button" className="ghost" onClick={() => start({ kind: 'cancel' })}>
              إلغاء الفاتورة
            </button>
          )}
        </div>
      )}

      {mode && (
        <form onSubmit={(event) => void submit(event)} noValidate>
          <h3>
            {mode.kind === 'cancel'
              ? 'إلغاء الفاتورة بقيد عكسى'
              : KIND_LABELS[mode.noteKind] + ' على الفاتورة'}
          </h3>
          <div className="form-row">
            <label>
              التاريخ
              <input
                type="date"
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                  touched();
                }}
              />
            </label>
            <label className="grow">
              السبب
              <input
                value={reason}
                maxLength={500}
                onChange={(event) => {
                  setReason(event.target.value);
                  touched();
                }}
              />
              <Hint>لماذا تُعدَّل فاتورة العميل: مرتجع، خصم لاحق، أو خطأ فى السعر.</Hint>
            </label>
          </div>
          {mode.kind === 'note' && (
            <div className="form-row">
              <label className="grow">
                البيان
                <input
                  value={description}
                  maxLength={500}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    touched();
                  }}
                />
              </label>
              <label>
                المبلغ
                <input
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => {
                    setAmount(event.target.value);
                    touched();
                  }}
                />
              </label>
            </div>
          )}
          {mode.kind === 'cancel' && (
            <p className="sub">
              يُرحَّل قيد عكسى كامل بتاريخ الإلغاء، وتبقى الفاتورة وقيدها كما صدرا مع حالة «ملغاة».
              لا رجوع عن الإلغاء.
            </p>
          )}
          {problems.map((problem, i) => (
            <p key={i} className="error">
              {problem}
            </p>
          ))}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy || !ready}>
              {busy ? 'جارى الترحيل…' : mode.kind === 'cancel' ? 'تأكيد الإلغاء' : 'ترحيل الإشعار'}
            </button>
            <button type="button" className="ghost" onClick={() => setMode(null)}>
              تراجع
            </button>
            <Hint block>
              يُسجَّل الإشعار فى حساب العميل ويؤثر على رصيده فورًا. بعد الترحيل لا يُعدَّل ولا يُحذف
              — الخطأ يُصحَّح بمستند عكسى، حتى يبقى تاريخ كل مبلغ واضحًا.
            </Hint>
          </div>
        </form>
      )}
      {!mode &&
        problems.map((problem, i) => (
          <p key={i} className="error">
            {problem}
          </p>
        ))}
    </div>
  );
}
